import * as DialogPrimitive from '@radix-ui/react-dialog'
import { CheckCheck } from 'lucide-react'
import { SheetContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { QueryState, EmptyState } from '@/components/states'
import { asList } from '@/api/hooks'
import { NotificationsA, notificationHref, useMarkRead, useMyNotifications, type StaffNotification } from '@/api/notifications'
import { ui, useUI } from '@/lib/store'
import { useT } from '@/i18n'
import { fmtRelative } from '@/lib/dates'
import { useGo } from '@/lib/routing'
import { cn } from '@/lib/utils'

export function NotificationsDrawer() {
  const open = useUI((s) => s.notificationsOpen)
  const t = useT()
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => ui.set({ notificationsOpen: v })}>
      <SheetContent title={t('notifications.title')} width="min(420px, 100vw)" headerExtra={open ? <MarkAll /> : null}>
        {open && <List />}
      </SheetContent>
    </DialogPrimitive.Root>
  )
}

function MarkAll() {
  const t = useT()
  const mark = useMarkRead()
  return (
    <Button variant="ghost" size="sm" actionId={NotificationsA.markRead} loading={mark.isPending} onClick={() => mark.mutate({ all: true })}>
      <CheckCheck /> {t('notifications.markAll')}
    </Button>
  )
}

function List() {
  const t = useT()
  const q = useMyNotifications()
  const go = useGo()
  const mark = useMarkRead()
  return (
    <div className="p-3">
      <QueryState query={q} actionId={NotificationsA.my} compact>
        {(data) => {
          const items = asList<StaffNotification>(data)
          if (!items.length) return <EmptyState mood="happy" compact title={t('notifications.empty')} />
          return (
            <ul className="flex flex-col gap-1">
              {items.map((n) => {
                const href = notificationHref(n)
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={cn('flex w-full flex-col gap-0.5 rounded-[10px] px-3 py-2.5 text-left hover:bg-sunken', !n.read_at && 'bg-brand-soft/40')}
                      onClick={() => {
                        if (!n.read_at) mark.mutate({ ids: [n.id] })
                        if (href) {
                          ui.set({ notificationsOpen: false })
                          go(href)
                        }
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {!n.read_at && <span className="size-2 shrink-0 rounded-full bg-brand" aria-label={t('notifications.unread')} />}
                        <span className={cn('text-[13.5px]', n.read_at ? 'text-ink' : 'font-semibold text-ink-strong')}>{n.title}</span>
                        {n.urgent && <span className="rounded bg-danger-soft px-1.5 text-[11px] font-medium text-danger">{t('notifications.urgent')}</span>}
                      </span>
                      {n.body && <span className="line-clamp-2 text-[12.5px] text-muted">{n.body}</span>}
                      <span className="text-[11.5px] text-muted">{fmtRelative(n.created_at)}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )
        }}
      </QueryState>
    </div>
  )
}
